/**
 * lib/render-policy/display-reviews.ts — customer-review selection resolver
 * (PIP lean rebuild, WS3/WS5). PURE function over pre-fetched rows — the PDP
 * route fetches a capped page of fin_reviews_raw + a ratings-only histogram
 * sample + the fin_review_rollup row and passes them in.
 *
 * Modes:
 *   'strict'  — only display_eligible=true rows render (post-moderation;
 *               the production go-live path once pip-review-moderation runs).
 *   'preview' — moderation hasn't run yet (moderation IS NULL) rows also
 *               render, flagged via summary.pendingModeration so the module
 *               can badge itself in dev. Rows an earlier moderation pass
 *               REJECTED (moderation set, display_eligible=false) never
 *               render in either mode.
 *
 * FALLBACK CONTRACT: empty input → empty reviews + null summary fields
 * (component self-hides — Real-or-Hidden Rule 3).
 *
 * OUTPUT CONTRACT: the returned rows carry render fields only. The
 * `moderation` blob and `display_eligible` flag are consumed by the gate here
 * and stripped — they are not part of what ships to the client (FRIDGE-255).
 */

export interface DisplayReviewRow {
  id: string;
  source: string;
  source_url: string | null;
  rating: number | null;
  title: string | null;
  body: string | null;
  author_name: string | null;
  author_verified: boolean | null;
  posted_at: string | null;
  helpful_count: number | null;
  review_imgs?: string[] | null;
  display_eligible?: boolean | null;
  moderation?: unknown | null;
  /** Set by the resolver when a Bazaarvoice incentive disclosure was stripped
   * from the body (owner QA 07-06). Kept on the row so a disclosure badge can
   * be added later without re-parsing. */
  incentivized?: boolean;
}

/** Bazaarvoice boilerplate disclosures prepended to bodies — 48.8k rows carry
 * "[This review was collected as part of a promotion.]". Strip at RENDER
 * (evidence rows stay raw, Layer-1 contract). */
const DISCLOSURE_PREFIX_RE =
  /^\s*\[\s*this review was (?:collected|written)[^\]]*\]\s*/i;

export function cleanReviewBody(body: string | null): { body: string | null; incentivized: boolean } {
  if (!body) return { body, incentivized: false };
  const stripped = body.replace(DISCLOSURE_PREFIX_RE, '');
  if (stripped === body) return { body, incentivized: false };
  const t = stripped.trim();
  return { body: t.length > 0 ? t : null, incentivized: true };
}

export type ReviewsMode = 'strict' | 'preview';

export interface RollupInput {
  sources: Array<{ source: string; rating: number | string | null; count: number }>;
  weighted_rating: number | string | null; // numeric columns arrive as strings via PostgREST
  total_reviews: number;
}

export interface ReviewsSummary {
  avgRating: number | null;
  totalRatings: number;
  /** Size of the full eligible, sorted corpus BEFORE the serialization cap —
   * what app/api/reviews/[fin] would return. The client fetches the corpus
   * only when eligibleCount > reviews.length. */
  eligibleCount: number;
  /** Index 0 = 5★ … index 4 = 1★. */
  histogram: [number, number, number, number, number];
  rollup: {
    sources: Array<{ source: string; label: string; rating: number | null; count: number }>;
    weightedRating: number | null;
    totalReviews: number;
  } | null;
  pendingModeration: boolean;
}

export interface ReviewsResult {
  reviews: DisplayReviewRow[];
  summary: ReviewsSummary;
}

const SOURCE_LABELS: Record<string, string> = {
  amazon: 'Amazon',
  keepa: 'Amazon',
  aj_madison: 'AJ Madison',
  bazaarvoice: 'AJ Madison',
  best_buy: 'Best Buy',
  best_buy_search: 'Best Buy',
  walmart: 'Walmart',
  walmart_search: 'Walmart',
  home_depot: 'Home Depot',
  lowes: "Lowe's",
  manufacturer: 'Manufacturer',
};

export function reviewSourceLabel(source: string): string {
  const known = SOURCE_LABELS[source];
  if (known) return known;
  return source
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

type EligibilityFields = { display_eligible?: boolean | null; moderation?: unknown | null };

/** Per-image moderation verdict written by scripts/pip-review-image-moderation.ts
 * into moderation.images[] (item 50 — photo allowlist before strips go strict).
 * verdict 'error' = the image could not be fetched/judged; fail-closed in
 * strict mode but retryable (the script re-judges 'error' entries on
 * --retry-errors). */
export interface ReviewImageVerdict {
  url: string;
  verdict: 'ok' | 'reject' | 'error';
  reasons: string[];
  model: string;
  at: string;
}

/** The moderation jsonb carries TWO independent passes:
 *  - text pass (pip-review-moderation): { verdict, reasons, model, at }
 *  - image pass (pip-review-image-moderation): { images: ReviewImageVerdict[] }
 * A blob holding ONLY the image pass must NOT count as a text verdict —
 * otherwise running image moderation first would silently flip every
 * text-pending row to "rejected" (moderation != null && !display_eligible). */
function hasTextVerdict(moderation: unknown): boolean {
  return (
    typeof moderation === 'object' &&
    moderation !== null &&
    'verdict' in (moderation as Record<string, unknown>)
  );
}

/** Rejected = a TEXT moderation verdict exists AND it kept the row ineligible. */
function isRejected(r: EligibilityFields): boolean {
  return hasTextVerdict(r.moderation) && r.display_eligible !== true;
}

function isEligible(r: EligibilityFields, mode: ReviewsMode): boolean {
  if (mode === 'strict') return r.display_eligible === true;
  return r.display_eligible === true || !hasTextVerdict(r.moderation);
}

function imageVerdicts(moderation: unknown): ReviewImageVerdict[] | null {
  if (typeof moderation !== 'object' || moderation === null) return null;
  const imgs = (moderation as { images?: unknown }).images;
  return Array.isArray(imgs) ? (imgs as ReviewImageVerdict[]) : null;
}

/**
 * Per-image allowlist gate (item 50). Returns the subset of a row's
 * review_imgs that may render in the given mode:
 *  - 'strict': ONLY images with a banked verdict === 'ok' (fail-closed —
 *    an unmoderated or errored photo never renders in production).
 *  - 'preview': unmoderated photos still render (dev preview keeps its
 *    current behavior); anything with a non-'ok' verdict is hidden.
 * Pure + exported so the moderation script and tests share the exact gate.
 */
export function displayReviewImgs(
  row: Pick<DisplayReviewRow, 'review_imgs' | 'moderation'>,
  mode: ReviewsMode
): string[] {
  const imgs = row.review_imgs ?? [];
  if (imgs.length === 0) return [];
  const verdicts = imageVerdicts(row.moderation);
  if (!verdicts) return mode === 'strict' ? [] : imgs;
  const byUrl = new Map(verdicts.map((v) => [v.url, v.verdict]));
  return imgs.filter((u) => {
    const v = byUrl.get(u);
    if (v === undefined) return mode !== 'strict'; // unjudged image on a judged row
    return v === 'ok';
  });
}

/** Histogram-sample rows: rating plus (optionally) the eligibility fields so
 * the sample can honor the same mode gating as the display rows. */
export type RatingSampleRow = Pick<DisplayReviewRow, 'rating'> &
  Partial<Pick<DisplayReviewRow, 'display_eligible' | 'moderation'>> & {
    rating: number | string | null;
  };

export function getDisplayReviews(input: {
  rows: DisplayReviewRow[] | null | undefined;
  /** Ratings-only sample across the WHOLE FIN (the display rows are capped). */
  ratingsSample?: RatingSampleRow[] | null;
  rollup?: RollupInput | null;
  mode: ReviewsMode;
  /** Serialization cap — how many resolved rows the caller may ship. The PDP
   * render path uses the default below; the full-corpus route
   * (app/api/reviews/[fin]) passes a high cap because its response is fetched
   * on demand, never serialized into every page's flight payload. */
  serializedMax?: number;
}): ReviewsResult {
  const { rows, ratingsSample, rollup, mode, serializedMax } = input;

  const eligible = (rows ?? [])
    .filter((r) => !isRejected(r) && isEligible(r, mode))
    .map((r) => {
      const cleaned = cleanReviewBody(r.body);
      const base = cleaned.incentivized ? { ...r, body: cleaned.body, incentivized: true } : r;
      // Item 50: photo strips only ever see allowlisted images (strict mode
      // fail-closed; preview keeps unmoderated photos visible in dev).
      const imgs = displayReviewImgs(base, mode);
      if ((base.review_imgs?.length ?? 0) === imgs.length) return base;
      return { ...base, review_imgs: imgs };
    });

  // Positive reviews lead (owner feedback 2026-07-06: helpful-first surfaced
  // 1-2★ reviews above the fold on products averaging 4.5★ — negative
  // reviews attract disproportionate helpful votes). Band by sentiment first
  // (4-5★ → unrated → 3★ → 1-2★), then helpful, photos, recency within a
  // band. The histogram still shows the honest distribution, and critical
  // reviews remain reachable below the fold.
  const sentimentBand = (r: DisplayReviewRow): number => {
    const v = toNum(r.rating);
    if (v === null) return 1;
    if (v >= 4) return 0;
    if (v >= 3) return 2;
    return 3;
  };
  const sorted = [...eligible].sort((a, b) => {
    const sa = sentimentBand(a);
    const sb = sentimentBand(b);
    if (sa !== sb) return sa - sb;
    const ha = a.helpful_count ?? 0;
    const hb = b.helpful_count ?? 0;
    if (ha !== hb) return hb - ha;
    const ia = (a.review_imgs?.length ?? 0) > 0 ? 1 : 0;
    const ib = (b.review_imgs?.length ?? 0) > 0 ? 1 : 0;
    if (ia !== ib) return ib - ia;
    const ta = a.posted_at ? Date.parse(a.posted_at) : 0;
    const tb = b.posted_at ? Date.parse(b.posted_at) : 0;
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });

  // Serialization cap (crawler-eye budget, FRIDGE-179 class; the 08/22/2026
  // red streak). Every row returned here on the RENDER path is serialized
  // into the RSC flight payload of every PDP/part/filter page — 76 full
  // bodies rode the wire on /filter/whirlpool-everydrop-edr4rxd1-water-filter
  // while the module rendered 10, pushing served HTML to 305 KB against the
  // 269 KB budget (40 was tried first: 282 KB; 24: still over — measured,
  // not assumed). The default now ships only the initial render window (the
  // module opens with 10; grouped view renders 5 per retailer), and EVERY
  // interaction that needs more — search, star filter, photos-only, sort,
  // topic select, show-more — triggers ONE fetch of the full corpus from
  // app/api/reviews/[fin], which runs this same resolver with a high
  // serializedMax. Net: the page gets LIGHTER and search gains COVERAGE (the
  // route serves up to 500 rows where the old flight capped at 80). The
  // histogram and rollup are computed from the SEPARATE whole-FIN ratings
  // sample above, so the stats stay honest — this cap trims payload, never
  // counts. Photo lists cap at 8 per row: the strip renders 4 and the
  // lightbox pages through what ships.
  const SERIALIZED_REVIEWS_MAX = 12;
  const SERIALIZED_IMGS_MAX = 8;
  const cap = serializedMax ?? SERIALIZED_REVIEWS_MAX;
  const reviews = sorted.slice(0, cap).map((r) =>
    (r.review_imgs?.length ?? 0) > SERIALIZED_IMGS_MAX
      ? { ...r, review_imgs: (r.review_imgs ?? []).slice(0, SERIALIZED_IMGS_MAX) }
      : r
  );

  // Histogram from the full-FIN ratings sample (same eligibility gating as the
  // display rows — a strict-mode histogram must not count unmoderated ratings);
  // fall back to the display rows.
  const ratingPool = (ratingsSample ?? rows ?? [])
    .filter((r) => !isRejected(r) && isEligible(r, mode))
    .map((r) => toNum(r.rating))
    .filter((v): v is number => v !== null && v >= 0.5);
  const histogram: [number, number, number, number, number] = [0, 0, 0, 0, 0];
  let sum = 0;
  for (const v of ratingPool) {
    const star = Math.min(5, Math.max(1, Math.round(v)));
    histogram[5 - star] += 1;
    sum += v;
  }
  const totalRatings = ratingPool.length;
  const avgRating = totalRatings > 0 ? Math.round((sum / totalRatings) * 10) / 10 : null;

  let rollupOut: ReviewsSummary['rollup'] = null;
  if (rollup && Array.isArray(rollup.sources) && rollup.sources.length > 0) {
    rollupOut = {
      sources: rollup.sources
        .map((s) => ({
          source: s.source,
          label: reviewSourceLabel(s.source),
          rating: toNum(s.rating),
          count: s.count ?? 0,
        }))
        .sort((a, b) => b.count - a.count),
      weightedRating: toNum(rollup.weighted_rating),
      totalReviews: rollup.total_reviews ?? 0,
    };
  }

  const pendingModeration = mode === 'preview' && reviews.some((r) => r.display_eligible !== true);

  // The gate has been applied; the moderation blob and the eligibility flag
  // are its INPUTS, not render data — <CustomerReviews> reads neither
  // (CustomerReviewRow has no such fields). Left on the rows they rode into
  // the PDP client payload once per review: on 08/15/2026 production PDPs
  // that was 11–17 KB of `moderation` plus 1.8 KB of `display_eligible` on an
  // 80-review page (FRIDGE-255, the FRIDGE-179 per-item class). Strip them
  // here so no caller can serialize them by accident.
  const rendered = reviews.map((r) => {
    const { moderation: _moderation, display_eligible: _eligible, ...row } = r;
    void _moderation;
    void _eligible;
    return row as DisplayReviewRow;
  });

  return {
    reviews: rendered,
    summary: {
      avgRating,
      totalRatings,
      eligibleCount: sorted.length,
      histogram,
      rollup: rollupOut,
      pendingModeration,
    },
  };
}
